import { c as _c } from "react/compiler-runtime";
import { Box, Card, CloseButton, Dialog, Flex, Heading, HStack, Icon, Portal, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { UserSignupEmailCaptureForm } from '@src/components/sco/Account/UserSignupEmailCaptureForm';
import { AdvisorsIllustration } from '@src/components/sco/Advisors/AdvisorsLandingIllustrations';
import { ADVISORS_ASK_PATH, ADVISORS_BASE } from '@src/components/sco/Advisors/constants';
import { FREE_WIL_MESSAGE_LIMIT } from '@src/components/sco/Advisors/model/access';
import { AdvisorsLandingBrowseBlock } from '@src/components/sco/Advisors/sections/AdvisorsLandingBrowseBlock.tsx';
import { ButtonLink } from '@src/components/sco/ButtonLink';
import { usePlanPriceLabels, withPrice } from '@src/components/sco/Conversion/controller/usePlanPriceLabels';
import { GenericContentBlockConfig, GenericContentBlocks } from '@src/components/sco/GenericLP/GenericContentBlock';
import GenericFAQ from '@src/components/sco/GenericLP/GenericFAQ';
import GenericHero from '@src/components/sco/GenericLP/GenericHero';
import { GenericHowItWorksItem } from '@src/components/sco/GenericLP/GenericHowItWorks';
import GenericSection from '@src/components/sco/GenericLP/GenericSection';
import { TextLink } from '@src/components/sco/TextLink';
import { useActiveContentContext } from '@src/contexts/ActiveContentContext';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useLoginHref } from '@src/hooks/useLoginHref';
import { useUniversalEffect } from '@src/hooks/useUniversalEffect.tsx';
import { TrackEvent } from '@src/lib/TrackEvent.tsx';
import React, { useCallback, useMemo, useState } from 'react';
import { LuChartLine, LuChevronRight, LuCircleCheck, LuRocket, LuUsers } from 'react-icons/lu';

/** Advisors marketing landing. Logged-out `/advisors` and `/advisors/landing` both render this. */
const variantKey = 'advisors';
const SECTION_BG = 'colorPalette.bg';

// ─── Page content (edit here) ─────────────────────────────────────────────────

const items: GenericHowItWorksItem[] = [{
  icon: LuRocket,
  text: 'Recruit',
  description: 'Find and onboard the right advisors.'
}, {
  icon: LuUsers,
  text: 'Compensate',
  description: 'Standard agreements, done right.'
}, {
  icon: LuChartLine,
  text: 'Manage',
  description: 'Keep advisors earning their equity.'
}];
const guestCta = (label: string, openDiagnostic: () => void): GenericContentBlockConfig['primaryCta'] => ({
  label,
  onClick: openDiagnostic
});
const authCta = (label: string, href: string): GenericContentBlockConfig['primaryCta'] => ({
  label,
  href
});
const buildMatchingBlocks = (openDiagnostic: () => void, isRegistered: boolean, prices: {
  pro: string | null;
  team: string | null;
}): GenericContentBlockConfig[] => [{
  eyebrow: 'Wil.ai',
  heading: 'An advisor who never sleeps.',
  paragraph: 'Wil.ai is trained on thousands of real Founder conversations and the experience of helping over a million startups. Ask anything, any hour, and get a straight answer.',
  listItems: ['Trained on real Founder conversations', 'Straight answers in seconds, 24/7', `${FREE_WIL_MESSAGE_LIMIT} free messages to start`],
  primaryCta: isRegistered ? authCta('Ask Wil.ai', ADVISORS_ASK_PATH) : guestCta('Ask Wil.ai', openDiagnostic),
  visual: <AdvisorsIllustration illustrationIndex={7} />
}, {
  eyebrow: 'Recruit',
  heading: 'Build a board, on purpose.',
  paragraph: 'Open a board seat, find marketplace advisors, and invite the ones who fit. You can message any marketplace advisor in Inbox — a board seat still needs an invite they accept.',
  listItems: ['Open a seat and describe what you need', 'Invite from Find advisors', 'Message them in Inbox anytime'],
  primaryCta: isRegistered ? authCta('Find advisors', `${ADVISORS_BASE}/directory`) : guestCta('Recruit a board', openDiagnostic),
  visual: <AdvisorsIllustration illustrationIndex={6} />
}, {
  eyebrow: 'Paid sessions',
  heading: 'Book a paid hour when you need one.',
  paragraph: 'Some marketplace advisors take paid video sessions. Book the hour and pay the rate — this is not a board seat and not a free message.',
  listItems: ['Only advisors who accept paid sessions', 'Rate shown before you book', 'Separate from equity board seats'],
  primaryCta: isRegistered ? authCta('Book a paid session', `${ADVISORS_BASE}/sessions/new`) : guestCta('Book a paid session', openDiagnostic),
  visual: <AdvisorsIllustration illustrationIndex={8} />
}, {
  eyebrow: 'Advisor team',
  heading: 'The Startups.com team, when you unlock it.',
  paragraph: `${withPrice('Advisor Team', prices.team)} is office hours and 1:1 with our in-house coaches. Marketplace advisors are a different path — recruit a seat or book a paid session.`,
  listItems: ['Office hours on Events', `Team 1:1 with ${withPrice('Advisor Team', prices.team)}`, 'Marketplace advisors are a separate path — message, recruit, or book'],
  primaryCta: isRegistered ? authCta('Open Advisors', ADVISORS_BASE) : guestCta('See how Advisors works', openDiagnostic),
  visual: <AdvisorsIllustration illustrationIndex={9} />
}];
const buildFaqItems = (prices: {
  pro: string | null;
  team: string | null;
}) => [{
  question: 'What is Wil.ai?',
  answer: 'An AI advisor trained on thousands of real Founder conversations and fifteen years of helping startups launch. Ask it anything about your startup and get a straight answer in seconds, at any hour.'
}, {
  question: 'When do I talk to a real advisor instead?',
  answer: "Ask Wil first. Message a marketplace advisor in Inbox, invite them to a board seat, or book a paid session. The Startups.com team's office hours and 1:1 are a separate Advisor Team unlock."
}, {
  question: 'What can advisors actually help with?',
  answer: 'Funding, pitch decks, customer acquisition, and product development are where we go deepest. If it is blocking your startup, it is fair game.'
}, {
  question: 'How fast can I get access?',
  answer: 'Wil is immediate after you create an account. From Advisors home you can message a marketplace advisor, invite them to a board seat, or book a paid session.'
}, {
  question: 'Is there a cost to get started?',
  answer: `Free to start, with your first ${FREE_WIL_MESSAGE_LIMIT} Wil messages included. Unlimited Wil comes with ${withPrice('Startups Pro', prices.pro)}. The Startups.com advisor team (office hours + team 1:1) is ${withPrice('Advisor Team', prices.team)}. Marketplace paid sessions are priced by the advisor.`
}];

// ─── Page ─────────────────────────────────────────────────────────────────────

export const AdvisorsLandingSection = () => {
  const activeContent = useActiveContentContext();
  const {
    currentUser
  } = useCurrentUser();
  const isRegistered = !!currentUser?.isRegistered;
  const planPrices = usePlanPriceLabels();
  const [diagnosticOpen, setDiagnosticOpen] = useState(false);
  const openDiagnostic = useCallback(() => {
    setDiagnosticOpen(true);
  }, []);
  const matchingBlocks = useMemo(() => buildMatchingBlocks(openDiagnostic, isRegistered, planPrices), [isRegistered, openDiagnostic]);
  useUniversalEffect(() => {
    activeContent.setSiteSection('advisors.landing');
    activeContent.setItem({
      __typename: 'LandingPage',
      title: 'Startup Advisors & Wil.ai',
      description: 'The advice you need, the moment you need it. A 24/7 AI advisor blended with real-world experts for funding, pitch decks, customers, and product.'
    });
    return () => {
      activeContent.setItem(null);
    };
  }, []);
  return <Box color={'fg'} width={'full'}>
      <TrackEvent action={'lpViewed'} onlyOnScreen={true} traits={{
      variantKey
    }} />

      <GenericHero gradientText={'The Moment you Need it.'} heading={'The Advice you Need, The Moment you Need it.'} subheading={'Ask Wil, recruit a board, book a paid hour, or unlock the Startups.com team.'}>
        <AdvisorsSignup />
      </GenericHero>
      {/* <GenericHowItWorks
        heading={'How it works'}
        background={SECTION_BG}
        items={items}
        cardProps={{ background: 'colorPalette.container' }}
       /> */}
      <GenericSection background={SECTION_BG} heading={'Ask Wil, message an advisor, recruit a board, or book an hour.'} subheading={'Wil is always on. Message a marketplace advisor, invite them to a board seat, or book a paid session. The Startups.com team is a separate Advisor Team unlock.'}>
        <GenericContentBlocks blocks={matchingBlocks} />
      </GenericSection>
      <GenericSection heading={'Meet the advisors'} subheading={'Founders and operators who have done it before - available for your advisor board or 1:1 sessions.'}>
        <AdvisorsLandingBrowseBlock />
        <BecomeAdvisorCta />
      </GenericSection>
      <GenericFAQ heading={'Frequent Questions'} items={buildFaqItems(planPrices)} subheading={"Want to know more? Here's some common questions & answers."} />

      {/* Mid-page CTAs open the same hero signup/diagnostic card in a modal. */}
      <Dialog.Root motionPreset={'scale'} open={diagnosticOpen} placement={'center'} scrollBehavior={'inside'} size={'xl'} onOpenChange={event => setDiagnosticOpen(event.open)}>
        <Portal>
          <Dialog.Backdrop backdropFilter={'blur(10px)'} bg={'colorPalette.bg/50'} />
          <Dialog.Positioner paddingX={[3, 4, 6]} paddingY={[4, 6]}>
            <Dialog.Content bg={'transparent'} boxShadow={'none'} maxWidth={'container.lg'} overflow={'visible'} width={'full'}>
              <Box position={'relative'}>
                <Dialog.CloseTrigger asChild position={'absolute'} right={3} top={3} zIndex={2}>
                  <CloseButton bg={'colorPalette.container'} borderColor={'colorPalette.border/50'} borderRadius={'full'} borderWidth={'1px'} size={'sm'} />
                </Dialog.CloseTrigger>
                <AdvisorsSignup id={undefined} />
              </Box>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>;
};
const AdvisorsSignup = t0 => {
  const $ = _c(16);
  const {
    id: t1
  } = t0;
  const id = t1 === undefined ? "signup" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["Nailing your pitch deck", "Raising your round", "Finding and winning customers", "Building the right product"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const HELP_TOPICS = t2 as const;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [{
      value: "1M+",
      label: "Startups"
    }, {
      value: "$20B",
      label: "Raised"
    }, {
      value: "23M",
      label: "Customers"
    }];
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const PROOF_STATS = t3 as const;
  const {
    currentUser
  } = useCurrentUser();
  const loginHref = useLoginHref();
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [5, 6, 7];
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      base: 1,
      md: 2
    };
    t6 = {
      base: 8,
      md: 7
    };
    $[3] = t5;
    $[4] = t6;
  } else {
    t5 = $[3];
    t6 = $[4];
  }
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = ["xl", "2xl", "3xl"];
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Heading as="h2" fontSize={t7} fontWeight="semibold" letterSpacing="-0.05em" lineHeight="1.3">Join over a Million Startups<br /><Box as="span" color="orange.fg">Who we've Helped</Box></Heading>;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Text color="fg.subtle" fontSize="sm" fontWeight="bold" letterSpacing="0.1em" textTransform="uppercase">What you can get help with</Text>;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <VStack align="stretch" gap={2.5} width="full">{t9}<VStack align="stretch" gap={2} width="full">{HELP_TOPICS.map(_temp)}</VStack></VStack>;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  let t11;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <VStack align="stretch" gap={5}>{t8}{t10}<Flex align="stretch" bg="bg.subtle" borderColor="border" borderRadius="lg" borderWidth="1px" justify="stretch" overflow="hidden" width="full">{PROOF_STATS.map(_temp2)}</Flex></VStack>;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  let t12;
  if ($[10] !== currentUser?.isRegistered || $[11] !== loginHref) {
    t12 = <Card.Body padding={t4}><SimpleGrid alignItems="center" columns={t5} gap={t6}>{t11}<VStack align="stretch" gap={4}>{currentUser?.isRegistered ? <Box textAlign="center"><Heading marginY={4}>You're logged in!</Heading><ButtonLink href="/advisors" variant="cta">Access Advisors <Icon as={LuChevronRight} /></ButtonLink></Box> : <VStack align="stretch"><UserSignupEmailCaptureForm containerProps={{
              bg: "colorPalette.card/50",
              width: "full"
            }} hubspotFormKey="scom-advisors-landing" returnToHref={ADVISORS_BASE} socialLoginButtonsProps={{
              buttonLayout: "google-only"
            }} submitButtonProps={{
              children: "Start with Advisors",
              size: "lg"
            }} /><Text color="fg.muted" fontSize="sm" textAlign="center">Already have an account? <TextLink href={loginHref}>Log in</TextLink></Text></VStack>}</VStack></SimpleGrid></Card.Body>;
    $[10] = currentUser?.isRegistered;
    $[11] = loginHref;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== id || $[14] !== t12) {
    t13 = <Card.Root bg="colorPalette.container" borderColor="colorPalette.border/50" borderRadius="2xl" borderWidth="1px" boxShadow="md" id={id} marginX="auto" maxWidth="container.lg" scrollMarginTop={24} variant="outline" width="full">{t12}</Card.Root>;
    $[13] = id;
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  return t13;
};

/** L11: the supply-side CTA on the `/advisors` landing. */
export const BecomeAdvisorCta = () => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      base: "column",
      md: "row"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      base: "center",
      md: "start"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <VStack align={t1} gap={1} textAlign={{
      base: "center",
      md: "start"
    }}><Heading as="h3" size="lg">Built a startup? Advise one.</Heading><Text color="fg.muted">Join founders' boards for equity, or offer paid 1:1 sessions.</Text></VStack>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Card.Root bg="colorPalette.container" borderColor="colorPalette.border/50" borderRadius="2xl" marginX="auto" maxWidth="container.lg" variant="outline" width="full"><Card.Body><Flex align="center" direction={t0} gap={4} justify="space-between">{t2}<ButtonLink flexShrink={0} href={`${ADVISORS_BASE}/become-a-startup-advisor`} variant="outline">Become an Advisor <Icon as={LuChevronRight} /></ButtonLink></Flex></Card.Body></Card.Root>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
function _temp(topic) {
  return <HStack key={topic} align="center" gap={2.5}><Icon as={LuCircleCheck} boxSize={4} color="orange.fg" flexShrink={0} /><Text color="fg" fontSize={["sm", "md"]} fontWeight="medium">{topic}</Text></HStack>;
}
function _temp2(stat, i) {
  return <Box key={stat.label} borderColor="border" borderLeftWidth={i === 0 ? 0 : "1px"} paddingX={[4, 5]} paddingY={3} textAlign="center" width="full"><Text fontSize={["xl", "2xl"]} fontWeight="black" letterSpacing="-0.02em" lineHeight={1}>{stat.value}</Text><Text color="fg.subtle" fontSize="2xs" letterSpacing="0.08em" marginTop={1} textTransform="uppercase">{stat.label}</Text></Box>;
}